// "{CODE} Breakdown" panel — the full description of one derived indicator.
//
// FM-1 moved the long-form copy off the headline card into here.
// FM-16 adds the RAG ranges behind the tier badge, flagged provisional until
// the fatigue lead confirms the numbers (PEAK-185).

import React from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { DerivedMetricKey, FatigueDerivedMetric } from '../data/types';
import { METRIC_DEFINITIONS, RAG_PROVISIONAL_NOTE, NOT_MEDICAL } from '../data/methodology';
import { NoteLine, RagRangeTable, TierBadge } from './primitives';
import { PanelSection, SidePanel } from './SidePanel';

export const MetricBreakdownPanel: React.FC<{
  metricKey: DerivedMetricKey | null;
  /** Current org/cohort value, used to highlight the live RAG band. */
  metric?: FatigueDerivedMetric;
  onClose: () => void;
}> = ({ metricKey, metric, onClose }) => {
  const def = metricKey ? METRIC_DEFINITIONS[metricKey] : null;

  return (
    <SidePanel
      open={Boolean(def)}
      onClose={onClose}
      title={def ? `${def.code} Breakdown` : ''}
      subtitle={def?.name}
      footer={
        <Button variant='outline' size='sm' onClick={onClose}>
          Close
        </Button>
      }
    >
      {def && metricKey && (
        <div className='flex flex-col'>
          {metric && (
            <PanelSection title='Current reading'>
              <div className='flex items-center justify-between gap-3'>
                <span className='text-2xl font-semibold tabular-nums'>
                  {metric.value}
                  <span className='ml-1.5 text-sm font-normal text-muted-foreground'>{metric.unit}</span>
                </span>
                <TierBadge tier={metric.riskTier} />
              </div>
            </PanelSection>
          )}

          <PanelSection title={def.breakdownHeading}>
            <p className='text-sm text-muted-foreground'>{def.breakdownIntro}</p>
            <p className='text-sm text-muted-foreground'>{def.whatIs}</p>
          </PanelSection>

          <PanelSection title={`How is ${def.code} calculated?`}>
            <p className='text-sm text-muted-foreground'>{def.howCalculated}</p>
            {def.howCalculatedList && (
              <ul className='flex flex-col gap-1'>
                {def.howCalculatedList.map((item) => (
                  <li key={item} className='flex gap-2 text-sm text-muted-foreground'>
                    <span aria-hidden>•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className='text-xs text-muted-foreground'>
              Relates to: <span className='font-medium'>{def.relatedTo}</span>
            </p>
          </PanelSection>

          {/* FM-16 */}
          <PanelSection title='RAG ranges'>
            <RagRangeTable metricKey={metricKey} currentValue={metric?.value} />
            <NoteLine>{RAG_PROVISIONAL_NOTE}</NoteLine>
          </PanelSection>

          <PanelSection title='What a higher score means'>
            <p className='text-sm text-muted-foreground'>{def.higherScoreLabel}</p>
            {def.higherScoreBullets.length > 0 && (
              <ul className='flex flex-col gap-1'>
                {def.higherScoreBullets.map((item) => (
                  <li key={item} className='flex gap-2 text-sm text-muted-foreground'>
                    <span aria-hidden>•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </PanelSection>

          <PanelSection title='Why this matters'>
            {def.whyMatters.map((para) => (
              <p key={para} className='text-sm text-muted-foreground'>
                {para}
              </p>
            ))}
          </PanelSection>

          <PanelSection title={`Use ${def.code} to help identify`}>
            <ul className='flex flex-col gap-1.5'>
              {def.useToIdentify.map((item) => (
                <li key={item} className='flex items-start gap-2 text-sm'>
                  <Check className='mt-0.5 size-4 shrink-0 text-success' aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </PanelSection>

          <PanelSection title='Scope'>
            <p className='text-sm text-muted-foreground'>{def.breakdownCaveat}</p>
            <NoteLine>{NOT_MEDICAL}</NoteLine>
          </PanelSection>
        </div>
      )}
    </SidePanel>
  );
};
